import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { type MemberRole, memberRoles } from "shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { restrictRoute } from "@/lib/routeBlocker";
import { useTRPC } from "@/lib/trpcClient";

export const Route = createFileRoute(
  "/_authed/_org/organization/people/all/$id/edit-role",
)({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/organization/people",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const { owner } = useRequiredRole();
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();

  const { data: user } = useSuspenseQuery(
    trpc.org.orgUser.queryOptions({
      userId: id,
    }),
  );

  const { mutate: updateRole, isPending } = useMutation(
    trpc.org.updateRole.mutationOptions({
      onError: (error) => {
        toast.error("Error updating role", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success(`Updated ${user.name}'s role`);
        queryClient.invalidateQueries(trpc.org.users.queryOptions());
        navigate({
          to: "/organization/people/all/$id",
        });
      },
    }),
  );

  const availableRoles = memberRoles.filter((role) => {
    if (role === "none") return false;
    if (role === "owner") return false;
    if (!owner && role === "admin") return false;
    return true;
  });

  const roleOptions = availableRoles.map((role) => ({
    label: getRoleDisplayName(role),
    value: role,
  }));

  const getDefaultRole = ():
    | "admin"
    | "bookkeeper"
    | "viewOnlyAdmin"
    | "member" => {
    const userRole = user.role;
    const validRoles = [
      "admin",
      "bookkeeper",
      "viewOnlyAdmin",
      "member",
    ] as const;

    if (
      validRoles.includes(userRole as any) &&
      availableRoles.includes(userRole as MemberRole)
    ) {
      return userRole as "admin" | "bookkeeper" | "viewOnlyAdmin" | "member";
    }
    return availableRoles[0] as
      | "admin"
      | "bookkeeper"
      | "viewOnlyAdmin"
      | "member";
  };

  const form = useAppForm({
    defaultValues: {
      role: getDefaultRole(),
    },
    onSubmit: ({ value }) => {
      updateRole({
        memberId: user.memberId,
        newRole: value.role as
          | "admin"
          | "bookkeeper"
          | "viewOnlyAdmin"
          | "member",
      });
    },
  });

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="pb-2">
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({ params: { id }, to: "/organization/people/all/$id" })
            }
            className="p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <SheetTitle>Edit Role</SheetTitle>
        </div>
        <SheetDescription>
          Update {user.name}'s role in the organization
        </SheetDescription>
      </SheetHeader>
      <form
        className="space-y-6 overflow-y-auto p-4"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="space-y-4">
          <div className="text-gray-600 text-sm">
            <strong>Current User:</strong> {user.name} ({user.email})
          </div>

          <form.AppField
            children={(field) => (
              <field.SelectField
                description="Select the role for this user"
                label="Role"
                options={roleOptions}
              />
            )}
            name="role"
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            onClick={() => navigate({ to: "/organization/people" })}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button disabled={isPending} type="submit">
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Role
          </Button>
        </div>
      </form>
    </div>
  );
}

function getRoleDisplayName(role: MemberRole): string {
  switch (role) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "bookkeeper":
      return "Bookkeeper";
    case "viewOnlyAdmin":
      return "View Only Admin";
    case "member":
      return "Member";
    case "none":
      return "None";
    default:
      return role;
  }
}
